import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Select,
  DatePicker,
  Alert,
  Typography,
  Empty,
  List,
  Avatar,
  Tag,
  Tooltip as AntTooltip,
  Skeleton,
  Button,
  Radio,
  Grid,
  Progress,
} from "antd";
import {
  PieChart,
  Pie,
  BarChart,
  Bar,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line as RechartsLine,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  RiseOutlined,
  FallOutlined,
  DollarCircleOutlined,
  WalletOutlined,
  CalculatorOutlined,
  SyncOutlined,
  ShoppingOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  TagsOutlined,
  InfoCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  MinusOutlined,
  ArrowRightOutlined,
  AlertOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSelection } from "../SelectionContext";
import SecureLogger from "../utils/secureLogger";
import { fetchWithCacheMetadata, buildCacheKey } from "../utils/offlineCache";
import { isOffline } from "../utils/offlineQueue";
import {
  DASHBOARD_DATA_KEYS,
  DASHBOARD_DATA_STATUS,
  buildDashboardDatasetStatus,
  createDashboardDataStatuses,
  isDashboardDataAvailable,
  resolveDashboardMetricStatus,
} from "./dashboardDataStatus";
import metricDefinitionsDocument from "./dashboardMetricDefinitions.json";
import {
  aggregateDashboardChart,
  formatDashboardChartGranularity,
  resolveDashboardChartGranularity,
  DASHBOARD_CHART_GRANULARITY,
} from "./dashboardChartGranularity";
import "./Dashboard.css";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { useBreakpoint } = Grid;

const QUICK_RANGE_OPTIONS = [
  { key: "today", label: "Aujourd'hui" },
  { key: "week", label: "7 j" },
  { key: "month", label: "30 j" },
  { key: "thisMonth", label: "Mois" },
];

const MAX_DESKTOP_EXPENSE_BARS = 8;
const COLORS = [
  "#2563eb",
  "#0f766e",
  "#d97706",
  "#7c3aed",
  "#0369a1",
  "#c2410c",
];
const METRIC_DEFINITIONS = metricDefinitionsDocument.metrics || {};

const createEmptyDashboardData = () => ({
  businessMetrics: null,
  totals: null,
  salesPurchasesChart: null,
  topSellingProducts: null,
  topCustomers: null,
  stockAlerts: null,
  paymentsChart: null,
  grossMargin: null,
  averageOrderValue: null,
  stockTurnover: null,
  expenseBreakdown: null,
  stockValuation: null,
});

const getQuickRangeValue = (key) => {
  switch (key) {
    case "today":
      return [dayjs(), dayjs()];
    case "week":
      return [dayjs().subtract(6, "days"), dayjs()];
    case "month":
      return [dayjs().subtract(29, "days"), dayjs()];
    case "thisMonth":
      return [dayjs().startOf("month"), dayjs().endOf("month")];
    default:
      return null;
  }
};

// --- Fonction utilitaire pour formater en devise (Exemple simple pour CFA) ---
const formatCurrency = (amount, currency = "CFA") => {
  // Ensure amount is a number, default to 0 if not
  const numericAmount = Number(amount);
  if (isNaN(numericAmount)) {
    // Return "N/A" or "0 CFA" based on preference for invalid input
    // Let's return formatted 0 for consistency on charts
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(0);
  }

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericAmount);
};

const DashboardDataStatusBadge = ({ dataStatus }) => {
  const status = dataStatus?.status || DASHBOARD_DATA_STATUS.UNAVAILABLE;
  const isCached = status === DASHBOARD_DATA_STATUS.CACHED;
  const isVerified = status === DASHBOARD_DATA_STATUS.VERIFIED;
  const updatedAt = dataStatus?.updatedAt
    ? dayjs(dataStatus.updatedAt)
    : null;
  const hasValidTimestamp = updatedAt?.isValid();
  const label = isCached
    ? `Cache${
        hasValidTimestamp ? ` · ${updatedAt.format("DD/MM HH:mm")}` : ""
      }`
    : isVerified
      ? "Vérifié"
      : "Indisponible";
  const tooltip = isCached && hasValidTimestamp
    ? `Dernière actualisation réussie : ${updatedAt.format("DD/MM/YYYY à HH:mm")}`
    : dataStatus?.message || label;

  return (
    <AntTooltip title={tooltip}>
      <span className={`dashboard-data-status dashboard-data-status--${status}`}>
        {label}
      </span>
    </AntTooltip>
  );
};

const MetricDefinitionTitle = ({ metricKey, label }) => {
  const definition = METRIC_DEFINITIONS[metricKey];
  if (!definition) return label;

  return (
    <span className="dashboard-metric-title">
      <span>{label || definition.label}</span>
      <AntTooltip
        title={
          <div className="dashboard-metric-definition">
            <strong>{definition.formula}</strong>
            <span>{definition.description}</span>
          </div>
        }
      >
        <InfoCircleOutlined
          className="dashboard-metric-title__info"
          aria-label={`Définition de ${label || definition.label}`}
        />
      </AntTooltip>
    </span>
  );
};

const MetricComparison = ({
  comparison,
  contextLabel = "vs période précédente",
  newLabel = "Nouvelle activité",
}) => {
  if (!comparison) return null;

  const trend = comparison.trend || "stable";
  const icon =
    trend === "up" || trend === "new" ? (
      <ArrowUpOutlined />
    ) : trend === "down" ? (
      <ArrowDownOutlined />
    ) : (
      <MinusOutlined />
    );
  const label =
    trend === "new"
      ? newLabel
      : comparison.percentageChange === null ||
          comparison.percentageChange === undefined
        ? "Base précédente nulle"
        : `${Math.abs(Number(comparison.percentageChange)).toLocaleString("fr-FR", {
            maximumFractionDigits: 1,
          })} % ${trend === "up" ? "de hausse" : trend === "down" ? "de baisse" : "stable"}`;

  return (
    <span className={`dashboard-metric-comparison dashboard-metric-comparison--${trend}`}>
      {icon}
      <span>{label}</span>
      <span className="dashboard-metric-comparison__context">
        {contextLabel}
      </span>
    </span>
  );
};

const DashboardMetricCard = ({
  kpi,
  loading,
  compact = false,
  onDrilldown,
}) => {
  const available = isDashboardDataAvailable(kpi.dataStatus);
  const definition = METRIC_DEFINITIONS[kpi.definitionKey || kpi.key];
  const formattedValue = available
    ? kpi.formatter
      ? kpi.formatter(kpi.value)
      : formatCurrency(kpi.value)
    : "—";

  return (
    <Card
      bordered={false}
      className={`dashboard-metric-card${compact ? " dashboard-metric-card--compact" : ""}`}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      ) : (
        <>
          <div className="dashboard-metric-card__head">
            <span
              className="dashboard-metric-card__icon"
              style={{ color: kpi.accent || "var(--color-primary)" }}
            >
              {kpi.icon}
            </span>
            <span className="dashboard-metric-card__scope">
              {definition?.scope === "current"
                ? "Actuel"
                : definition?.scope === "closing"
                  ? "Fin de période"
                  : "Période"}
            </span>
          </div>
          <span className="dashboard-metric-card__label">
            <MetricDefinitionTitle
              metricKey={kpi.definitionKey || kpi.key}
              label={kpi.label}
            />
          </span>
          <strong
            className="dashboard-metric-card__value"
            style={{ color: kpi.accent || "var(--text-primary)" }}
          >
            {formattedValue}
          </strong>
          {available && kpi.meta ? (
            <span className="dashboard-metric-card__meta">{kpi.meta}</span>
          ) : null}
          {available && Number.isFinite(Number(kpi.coverage)) ? (
            <Tag
              className="dashboard-metric-card__coverage"
              color={Number(kpi.coverage) >= 95 ? "blue" : "orange"}
            >
              Coûts figés : {Number(kpi.coverage).toFixed(1)} %
            </Tag>
          ) : null}
          {available ? (
            <MetricComparison
              comparison={kpi.comparison}
              contextLabel={kpi.comparisonContext}
              newLabel={kpi.comparisonNewLabel}
            />
          ) : null}
          <div className="dashboard-metric-card__footer">
            <DashboardDataStatusBadge dataStatus={kpi.dataStatus} />
            {definition?.drilldown && onDrilldown ? (
              <Button
                type="link"
                size="small"
                className="dashboard-metric-card__link"
                onClick={() => onDrilldown(definition.drilldown)}
              >
                Voir le détail <ArrowRightOutlined />
              </Button>
            ) : null}
          </div>
        </>
      )}
    </Card>
  );
};

const STOCK_BREAKDOWN_LABELS = Object.freeze({
  rawMaterials: "Matières premières",
  semiFinished: "Produits semi-finis",
  finishedGoods: "Produits finis",
  merchandise: "Marchandises",
});

const StockValuationDetails = ({ valuation, dataStatus }) => {
  if (
    !isDashboardDataAvailable(dataStatus) ||
    !valuation?.breakdown
  ) {
    return null;
  }

  const quality = valuation.quality || {};
  const warnings = [];
  if (!quality.lotDataAvailable && quality.trackedStockLineCount > 0) {
    warnings.push("lots indisponibles : valorisation de secours au CMP");
  }
  if (quality.lotMismatchCount > 0) {
    warnings.push(
      `${quality.lotMismatchCount} écart${
        quality.lotMismatchCount > 1 ? "s" : ""
      } stock/lots`,
    );
  }
  if (quality.orphanLotStockCount > 0) {
    warnings.push(
      `${quality.orphanLotStockCount} lot${
        quality.orphanLotStockCount > 1 ? "s" : ""
      } sans ligne de stock correspondante`,
    );
  }
  if (quality.zeroCostCount > 0) {
    warnings.push(
      `${quality.zeroCostCount} ligne${
        quality.zeroCostCount > 1 ? "s" : ""
      } avec coût nul ou incomplet`,
    );
  }
  if (quality.negativeStockCount > 0) {
    warnings.push(
      `${quality.negativeStockCount} stock${
        quality.negativeStockCount > 1 ? "s" : ""
      } négatif${quality.negativeStockCount > 1 ? "s" : ""}`,
    );
  }
  if (quality.missingSalePriceCount > 0) {
    warnings.push(
      `${quality.missingSalePriceCount} article${
        quality.missingSalePriceCount > 1 ? "s" : ""
      } vendable${quality.missingSalePriceCount > 1 ? "s" : ""} sans prix de vente`,
    );
  }
  if (quality.excludedUnlimitedStockCount > 0) {
    warnings.push(
      `${quality.excludedUnlimitedStockCount} stock illimité exclu`,
    );
  }
  if (quality.excludedVariantParentStockCount > 0) {
    warnings.push(
      `${quality.excludedVariantParentStockCount} parent de variantes exclu`,
    );
  }
  if (Number(quality.thresholdCoverageRate) < 100) {
    warnings.push(
      `${Number(quality.thresholdUnconfiguredCount || 0)} article${
        Number(quality.thresholdUnconfiguredCount || 0) > 1 ? "s" : ""
      } sans seuil d’alerte`,
    );
  }

  const valuationMoment = valuation.valuationAsOf
    ? dayjs(valuation.valuationAsOf)
    : null;
  const valuationTimestamp = valuationMoment?.isValid()
    ? valuationMoment.format("DD/MM/YYYY à HH:mm")
    : null;

  return (
    <Card
      bordered={false}
      className="dashboard-stock-valuation"
      title="Composition du stock au coût"
      extra={
        valuationTimestamp ? (
          <AntTooltip title="Cette valorisation représente le stock actuel et ne dépend pas de la période sélectionnée.">
            <span className="dashboard-stock-valuation__timestamp">
              Au {valuationTimestamp}
            </span>
          </AntTooltip>
        ) : null
      }
    >
      <div className="dashboard-stock-valuation__breakdown">
        {Object.entries(STOCK_BREAKDOWN_LABELS).map(([key, label]) => {
          const segment = valuation.breakdown[key] || {};
          return (
            <div className="dashboard-stock-valuation__segment" key={key}>
              <span>{label}</span>
              <strong>{formatCurrency(segment.value || 0)}</strong>
              <small>
                {Number(segment.stockLineCount || 0)} ligne
                {Number(segment.stockLineCount || 0) > 1 ? "s" : ""} de stock
              </small>
            </div>
          );
        })}
      </div>

      <Alert
        className="dashboard-stock-valuation__quality"
        type={warnings.length > 0 ? "warning" : "success"}
        showIcon
        message={
          warnings.length > 0
            ? "Contrôles de valorisation à examiner"
            : "Stocks et lots réconciliés"
        }
        description={
          warnings.length > 0
            ? warnings.join(" · ")
            : "Les stocks suivis par lots sont couverts et aucun coût nul ou stock négatif n’a été détecté."
        }
      />
      <div className="dashboard-stock-valuation__thresholds">
        <div>
          <span>Couverture des seuils</span>
          <strong>{Number(quality.thresholdCoverageRate || 0).toFixed(1)} %</strong>
        </div>
        <Progress
          percent={Number(quality.thresholdCoverageRate || 0)}
          showInfo={false}
          strokeColor="#2563eb"
          trailColor="var(--bg-tertiary)"
        />
        <small>
          {Number(quality.thresholdConfiguredCount || 0)} configuré(s) sur{" "}
          {Number(quality.thresholdEligibleStockLineCount || 0)} article(s)
          physique(s) suivi(s).
        </small>
      </div>
      {Number(quality.fallbackValuation || 0) > 0 ? (
        <Text type="secondary" className="dashboard-stock-valuation__fallback">
          Part valorisée au CMP de secours : {formatCurrency(quality.fallbackValuation)}
        </Text>
      ) : null}
    </Card>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { selectedCompany, selectedWarehouse, setSelectedWarehouse } =
    useSelection();
  const { user } = useAuth(); // Get user from AuthContext
  const screens = useBreakpoint();
  const isMobileView = !screens.md;
  // const companyId = user?.company_id; // No longer needed for this logic

  // --- États pour les Filtres ---
  // Remove local warehouses state fetched by API
  // const [warehouses, setWarehouses] = useState([]);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(29, "days"),
    dayjs(),
  ]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // --- DERIVED State for Warehouse Filter based on AuthContext ---
  const warehousesForFilter = useMemo(() => {
    if (!user || !Array.isArray(user.assigned_warehouses) || !selectedCompany) {
      return [];
    }
    // Filter the user's assigned warehouses by the currently selected company
    return user.assigned_warehouses.filter(
      (wh) => wh.company_id === selectedCompany
    );
  }, [user, selectedCompany]);
  // --- End DERIVED State ---

  const selectedWarehouseDetails = useMemo(() => {
    if (!Array.isArray(user?.assigned_warehouses) || !selectedWarehouse) {
      return null;
    }

    return (
      user.assigned_warehouses.find(
        (wh) => Number(wh.id) === Number(selectedWarehouse)
      ) || null
    );
  }, [selectedWarehouse, user?.assigned_warehouses]);

  const selectedCompanyName = useMemo(() => {
    if (!selectedCompany) {
      return "Aucune entreprise";
    }

    const warehouseForCompany = Array.isArray(user?.assigned_warehouses)
      ? user.assigned_warehouses.find(
          (wh) => Number(wh.company_id) === Number(selectedCompany)
        )
      : null;

    return (
      warehouseForCompany?.company_name ||
      user?.company?.name ||
      `Entreprise #${selectedCompany}`
    );
  }, [selectedCompany, user?.assigned_warehouses, user?.company?.name]);

  const selectedWarehouseName = useMemo(() => {
    if (selectedWarehouseDetails?.name) {
      return selectedWarehouseDetails.name;
    }

    if (selectedWarehouse) {
      return `Établissement #${selectedWarehouse}`;
    }

    if (warehousesForFilter.length > 1) {
      return "Tous les établissements";
    }

    if (warehousesForFilter.length === 1) {
      return warehousesForFilter[0].name;
    }

    return "Aucun établissement";
  }, [selectedWarehouse, selectedWarehouseDetails, warehousesForFilter]);

  // --- États pour les Données et le Chargement ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(createEmptyDashboardData);
  const [dashboardDataStatus, setDashboardDataStatus] = useState(() =>
    createDashboardDataStatuses(),
  );

  // Nouvel état pour suivre les catégories désactivées dans la légende du PieChart
  const [inactiveCategories, setInactiveCategories] = useState([]);
  // Nouvel état pour l'effet de survol du PieChart
  const [activePieIndex, setActivePieIndex] = useState(null);

  // State for Sales/Purchases chart line visibility
  const [salesPurchasesFilter, setSalesPurchasesFilter] = useState("Tous"); // "Tous", "Ventes", "Achats"

  // State for Payments chart line visibility
  const [paymentsFilter, setPaymentsFilter] = useState("Tous"); // "Tous", "Entrées", "Sorties"
  const isMountedRef = useRef(true);
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    // StrictMode rejoue le montage après son nettoyage en développement.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const chartGranularity = useMemo(
    () =>
      resolveDashboardChartGranularity(
        dateRange?.[0],
        dateRange?.[1],
      ),
    [dateRange],
  );

  // --- Fonction utilitaire pour formater la date sur l'axe X ---
  const formatDateTick = useCallback(
    (tickItem) => {
      const date = dayjs(tickItem);
      if (chartGranularity === DASHBOARD_CHART_GRANULARITY.MONTH) {
        return date.format("MMM YYYY");
      }
      return date.format("DD/MM");
    },
    [chartGranularity],
  );

  // --- Fonction utilitaire pour formater les valeurs sur l'axe Y ---
  const formatYAxisTick = useCallback((tickItem) => {
    // Simplifier les grands nombres (ex: 1500000 -> 1.5M)
    if (tickItem >= 1000000) {
      return `${(tickItem / 1000000).toFixed(1)}M`;
    }
    if (tickItem >= 1000) {
      return `${(tickItem / 1000).toFixed(0)}k`;
    }
    return tickItem;
  }, []);

  // --- Fonction pour charger toutes les données du dashboard ---
  const fetchDashboardData = useCallback(async () => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;
    const isStaleRequest = () =>
      !isMountedRef.current || requestId !== latestRequestIdRef.current;

    // Use selectedCompany as the definitive source for the current company ID
    const currentCompanyId = selectedCompany;
    if (
      !currentCompanyId ||
      !dateRange ||
      dateRange.length !== 2 ||
      !dateRange[0] ||
      !dateRange[1]
    ) {
      SecureLogger.debug("Dashboard Fetch: Prérequis non remplis pour le chargement des données");
      // Reset data to initial state if prerequisites are not met
      if (!isStaleRequest()) {
        setDashboardData(createEmptyDashboardData());
        setDashboardDataStatus(
          createDashboardDataStatuses(DASHBOARD_DATA_STATUS.UNAVAILABLE, {
            message: "Sélectionnez une entreprise et une période valides.",
          }),
        );
        setLoading(false); // Ensure loading is turned off
      }
      return;
    }

    if (isStaleRequest()) {
      return;
    }

    setLoading(true);
    setError(null);
    const params = {
      companyId: currentCompanyId,
      startDate: dateRange[0].format("YYYY-MM-DD"),
      endDate: dateRange[1].format("YYYY-MM-DD"),
      // Ensure warehouseId is a number if selected, otherwise undefined
      warehouseId: selectedWarehouse ? Number(selectedWarehouse) : undefined,
    };

    SecureLogger.debug("Dashboard Fetch: Début du chargement des données du tableau de bord");

    try {
      const offlineMode = isOffline();

      // ✅ Endpoint agrégé (1 seul call HTTP) pour éviter le burst initial sur le pool MySQL
      // Fallback automatique sur l'ancienne stratégie (11 endpoints) si nécessaire.
      try {
        const bulkParams = {
          ...params,
          topProductsLimit: 5,
          topCustomersLimit: 5,
          stockAlertsLimit: 10,
        };

        const bulkCacheKey = buildCacheKey("dashboard:bulk", bulkParams);
        const bulkResult = await fetchWithCacheMetadata(
          bulkCacheKey,
          async () => {
            const response = await axios.get("/api/dashboard/bulk", {
              params: bulkParams,
            });
            return response.data;
          },
          null
        );
        const bulkPayload = bulkResult.data;

        if (!bulkPayload || !bulkPayload.data) {
          throw new Error(
            offlineMode
              ? "Données non disponibles hors ligne"
              : "Données bulk indisponibles"
          );
        }

        const bulkData = bulkPayload.data || {};

        // Validations minimales (comme l'ancien flow)
        if (
          bulkData.salesPurchasesChart &&
          (!bulkData.salesPurchasesChart.labels ||
            !Array.isArray(bulkData.salesPurchasesChart.labels))
        ) {
          SecureLogger.warn("Structure invalide pour salesPurchasesChart (bulk)");
          bulkData.salesPurchasesChart = null;
        }
        if (
          bulkData.paymentsChart &&
          (!bulkData.paymentsChart.labels ||
            !Array.isArray(bulkData.paymentsChart.labels))
        ) {
          SecureLogger.warn("Structure invalide pour paymentsChart (bulk)");
          bulkData.paymentsChart = null;
        }
        if (
          bulkData.expenseBreakdown &&
          !Array.isArray(bulkData.expenseBreakdown)
        ) {
          SecureLogger.warn("Structure invalide pour expenseBreakdown (bulk)");
          bulkData.expenseBreakdown = null;
        }
        if (bulkData.stockValuation) {
          if (
            bulkData.stockValuation.totalStockValueCost === undefined ||
            bulkData.stockValuation.totalStockValueSale === undefined
          ) {
            SecureLogger.warn("Structure invalide pour stockValuation (bulk)");
            bulkData.stockValuation = null;
          }
        }

        if (isStaleRequest()) {
          return;
        }

        const nextDashboardData = {
          businessMetrics: bulkData.businessMetrics ?? null,
          totals: bulkData.totals ?? null,
          salesPurchasesChart: bulkData.salesPurchasesChart ?? null,
          topSellingProducts: bulkData.topSellingProducts ?? null,
          topCustomers: bulkData.topCustomers ?? null,
          stockAlerts: bulkData.stockAlerts ?? null,
          paymentsChart: bulkData.paymentsChart ?? null,
          grossMargin: bulkData.grossMargin ?? null,
          averageOrderValue: bulkData.averageOrderValue ?? null,
          stockTurnover: bulkData.stockTurnover ?? null,
          expenseBreakdown: bulkData.expenseBreakdown ?? null,
          stockValuation: bulkData.stockValuation ?? null,
        };
        const nextDashboardDataStatus = createDashboardDataStatuses();

        DASHBOARD_DATA_KEYS.forEach((key) => {
          nextDashboardDataStatus[key] = buildDashboardDatasetStatus({
            value: nextDashboardData[key],
            sourceStatus: bulkResult.status,
            updatedAt: bulkResult.updatedAt,
          });
        });

        setDashboardData(nextDashboardData);
        setDashboardDataStatus(nextDashboardDataStatus);

        if (
          bulkPayload._errors &&
          Array.isArray(bulkPayload._errors) &&
          bulkPayload._errors.length > 0
        ) {
          if (isStaleRequest()) {
            return;
          }
          setError(
            `Certaines données n'ont pas pu être chargées: ${bulkPayload._errors.join(
              "; "
            )}`
          );
        }

        SecureLogger.debug("Dashboard Fetch: Chargement via /api/dashboard/bulk OK");
        return;
      } catch (bulkErr) {
        SecureLogger.warn(
          `Chargement groupé indisponible, reprise par indicateur : ${bulkErr.message}`
        );
      }

      const endpoints = [
        { key: "businessMetrics", url: "/api/dashboard/business-metrics" },
        { key: "totals", url: "/api/dashboard/totals" },
        {
          key: "salesPurchasesChart",
          url: "/api/dashboard/sales-purchases-chart",
        },
        {
          key: "topSellingProducts",
          url: "/api/dashboard/top-selling-products",
          params: { limit: 5 },
        },
        {
          key: "topCustomers",
          url: "/api/dashboard/top-customers",
          params: { limit: 5 },
        },
        {
          key: "stockAlerts",
          url: "/api/dashboard/stock-alerts",
          params: { limit: 10 },
        },
        { key: "paymentsChart", url: "/api/dashboard/payments-chart" },
        { key: "grossMargin", url: "/api/dashboard/gross-margin" },
        { key: "averageOrderValue", url: "/api/dashboard/average-order-value" },
        { key: "stockTurnover", url: "/api/dashboard/stock-turnover" },
        { key: "expenseBreakdown", url: "/api/dashboard/expense-breakdown" },
        { key: "stockValuation", url: "/api/dashboard/stock-valuation" },
      ];
      const requests = endpoints.map(async (ep) => {
        const cacheKey = buildCacheKey(`dashboard:${ep.key}`, {
          ...params,
          ...(ep.params || {}),
        });

        try {
          const cacheResult = await fetchWithCacheMetadata(
            cacheKey,
            async () => {
              const response = await axios.get(ep.url, {
                params: { ...params, ...(ep.params || {}) },
              });
              return response.data;
            },
            null
          );
          const payload = cacheResult.data;

          if (!payload) {
            return {
              key: ep.key,
              error: true,
              message: offlineMode
                ? "Données non disponibles hors ligne"
                : "Données indisponibles",
              status: offlineMode ? "offline" : undefined,
            };
          }

          return {
            key: ep.key,
            data: payload,
            cacheStatus: cacheResult.status,
            updatedAt: cacheResult.updatedAt,
          };
        } catch (err) {
          SecureLogger.error(
            `API Error for ${ep.key}: ${err.message} (Status: ${err.response?.status || 'N/A'})`
          );
          return {
            key: ep.key,
            error: true,
            message:
              err.response?.data?.error ||
              err.response?.data?.message ||
              `Erreur chargement ${ep.key}: ${err.message}`,
            status: err.response?.status,
          };
        }
      });

      const results = await Promise.all(requests);

      SecureLogger.debug("Dashboard Fetch: Données reçues des APIs");

      const newData = {};
      const newDataStatus = createDashboardDataStatuses();
      const errors = []; // Collect specific errors

      results.forEach((res, index) => {
        const key = res.key || endpoints[index].key;
        newDataStatus[key] = {
          sourceStatus: res.error
            ? DASHBOARD_DATA_STATUS.UNAVAILABLE
            : res.cacheStatus || DASHBOARD_DATA_STATUS.VERIFIED,
          updatedAt: res.updatedAt,
          message: res.error ? res.message : null,
        };
        if (res.error) {
          SecureLogger.error(`Erreur API traitée pour ${key}: ${res.message} (Status: ${res.status || "N/A"})`);
          newData[key] = null; // Ensure data is null on error
          errors.push(`${key}: ${res.message}`); // Collect error message
        } else if (
          res.data &&
          res.data.data !== undefined &&
          res.data.data !== null
        ) {
          // Primary case: data is correctly nested
          newData[key] = res.data.data;
          // Add specific data structure validations if necessary
          if (key === "salesPurchasesChart" || key === "paymentsChart") {
            if (!res.data.data.labels || !Array.isArray(res.data.data.labels)) {
              SecureLogger.warn(`Structure invalide pour ${key}: 'labels' manquant ou n'est pas un tableau`);
              newData[key] = null;
              errors.push(`${key}: Structure de données invalide (labels)`);
            }
          } else if (
            key === "expenseBreakdown" &&
            !Array.isArray(res.data.data)
          ) {
            SecureLogger.warn(`Structure invalide pour ${key}: la donnée n'est pas un tableau`);
            newData[key] = null;
            errors.push(
              `${key}: Structure de données invalide (attendu tableau)`
            );
          } else if (key === "stockValuation") {
            if (
              res.data.data.totalStockValueCost === undefined ||
              res.data.data.totalStockValueSale === undefined
            ) {
              SecureLogger.warn(`Structure invalide pour ${key}: champs manquants`);
              newData[key] = null;
              errors.push(`${key}: Structure de données invalide`);
            } else {
              newData[key] = res.data.data;
            }
          }
          // ... other validations
        } else if (
          key === "totals" &&
          res.data &&
          res.data.totalSales !== undefined
        ) {
          // Specific fallback for 'totals' if not nested under 'data'
          SecureLogger.warn(`Structure 'totals' détectée directement dans res.data pour ${key}. Adaptation`);
          newData[key] = {
            // Reconstruct the object as expected by the frontend
            totalSales: res.data.totalSales,
            totalPurchases: res.data.totalPurchases,
            totalExpenses: res.data.totalExpenses,
            totalPaymentsReceived: res.data.totalPaymentsReceived,
            totalPaymentsSent: res.data.totalPaymentsSent,
            totalTreasuryBalance: res.data.totalTreasuryBalance,
            // Add other expected fields if any
          };
        } else {
          // Case where res.data is present but res.data.data is missing/null (excluding handled 'totals' case)
          SecureLogger.warn(`Données attendues manquantes, nulles ou vides pour ${key}`);
          newData[key] = null; // Set to null for consistency
          // Optionally consider this an error depending on the endpoint's importance
          // errors.push(`${key}: Données manquantes ou vides`);
        }
      });

      SecureLogger.debug("Dashboard Fetch: Données traitées avec succès");
      if (errors.length > 0) {
        SecureLogger.debug(`Dashboard Fetch: ${errors.length} erreur(s) collectée(s)`);
      }

      if (isStaleRequest()) {
        return;
      }

      DASHBOARD_DATA_KEYS.forEach((key) => {
        const source = newDataStatus[key] || {};
        newDataStatus[key] = buildDashboardDatasetStatus({
          value: newData[key],
          sourceStatus:
            source.sourceStatus || DASHBOARD_DATA_STATUS.UNAVAILABLE,
          updatedAt: source.updatedAt,
          message: source.message,
        });
      });

      setDashboardData(newData); // Update state with potentially partial data
      setDashboardDataStatus(newDataStatus);

      if (errors.length > 0) {
        // Aggregate error messages for display
        const aggregateErrorMessage = errors.join("; ");
        setError(
          `Certaines données n'ont pas pu être chargées: ${aggregateErrorMessage}`
        );
      }
    } catch (err) {
      // Catch unexpected errors (e.g., network issues before requests start, synchronous errors)
      SecureLogger.error(`Erreur générale non interceptée dans fetchDashboardData: ${err.message}`);
      if (!isStaleRequest()) {
        setError(
          "Une erreur inattendue est survenue lors du chargement: " + err.message
        );
        // Reset all data on catastrophic failure
        setDashboardData(createEmptyDashboardData());
        setDashboardDataStatus(
          createDashboardDataStatuses(DASHBOARD_DATA_STATUS.UNAVAILABLE, {
            message: err.message,
          }),
        );
      }
    } finally {
      if (!isStaleRequest()) {
        setLoading(false); // Ensure loading is always turned off
      }
    }
    // Dependencies: selectedCompany, selectedWarehouse, dateRange (which are state variables)
  }, [selectedCompany, selectedWarehouse, dateRange]);

  // Effect to trigger data fetching when filters change
  useEffect(() => {
    fetchDashboardData();
    // The dependency array correctly lists the inputs to fetchDashboardData
  }, [fetchDashboardData]);

  const handleWarehouseChange = useCallback((value) => {
    setSelectedWarehouse(value === undefined ? null : Number(value));
  }, [setSelectedWarehouse]);

  const handleDateChange = useCallback((dates) => {
    // `dates` is an array [dayjsObject, dayjsObject] or null if cleared
    if (dates && dates.length === 2 && dates[0] && dates[1]) {
      setDateRange(dates);
    } else if (!dates) {
      // Handle clear case explicitly
      // Option 1: Reset to default (e.g., last 30 days)
      // setDateRange([dayjs().subtract(29, "days"), dayjs()]);
      // Option 2: Set to null/empty to indicate no selection (might require handling in fetchDashboardData)
      setDateRange(null); // Or an empty array [] depending on how you want to handle "no date range"
      SecureLogger.debug("Date range cleared");
      // Note: fetchDashboardData logic needs to handle null dateRange gracefully
    }
    // If dates array is malformed (e.g., only one date), do nothing or reset?
  }, []);

  const activeQuickRangeKey = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return null;
    }

    const match = QUICK_RANGE_OPTIONS.find(({ key }) => {
      const presetRange = getQuickRangeValue(key);
      return (
        presetRange &&
        dateRange[0].isSame(presetRange[0], "day") &&
        dateRange[1].isSame(presetRange[1], "day")
      );
    });

    return match?.key || null;
  }, [dateRange]);

  const applyQuickRange = useCallback((key) => {
    const range = getQuickRangeValue(key);
    if (range) {
      setDateRange(range);
    }
  }, []);

  const currentDateRangeLabel = useMemo(() => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return "Période personnalisée";
    }

    if (dateRange[0].isSame(dateRange[1], "day")) {
      return dateRange[0].format("DD MMM YYYY");
    }

    if (!dateRange[0].isSame(dateRange[1], "year")) {
      return `${dateRange[0].format("DD MMM YYYY")} - ${dateRange[1].format(
        "DD MMM YYYY",
      )}`;
    }

    return `${dateRange[0].format("DD MMM")} - ${dateRange[1].format(
      "DD MMM YYYY"
    )}`;
  }, [dateRange]);

  const previousDateRangeLabel = useMemo(() => {
    const previousPeriod = dashboardData.businessMetrics?.previousPeriod;
    if (!previousPeriod?.startDate || !previousPeriod?.endDate) return null;
    return `${dayjs(previousPeriod.startDate).format("DD MMM YYYY")} - ${dayjs(
      previousPeriod.endDate,
    ).format("DD MMM YYYY")}`;
  }, [dashboardData.businessMetrics]);

  const handleMetricDrilldown = useCallback(
    (path) => {
      if (!path) return;
      const query = new URLSearchParams();
      if (dateRange?.[0]) query.set("startDate", dateRange[0].format("YYYY-MM-DD"));
      if (dateRange?.[1]) query.set("endDate", dateRange[1].format("YYYY-MM-DD"));
      if (selectedWarehouse) query.set("warehouseId", String(selectedWarehouse));
      navigate(`${path}${query.toString() ? `?${query.toString()}` : ""}`);
    },
    [dateRange, navigate, selectedWarehouse],
  );

  // --- MODIFICATION 4: Rendre la transformation de données plus robuste ---
  const transformChartData = useCallback((
    chartRawData,
    valueField1,
    valueField2,
    type1,
    type2
  ) => {
    if (
      !chartRawData ||
      !chartRawData.labels ||
      !Array.isArray(chartRawData.labels)
    ) {
      return []; // Return empty array if data structure is invalid
    }
    const { labels, [valueField1]: data1, [valueField2]: data2 } = chartRawData;

    // Ensure data arrays exist and have the same length as labels
    if (
      !data1 ||
      !data2 ||
      data1.length !== labels.length ||
      data2.length !== labels.length
    ) {
      SecureLogger.warn("Mismatch in chart data arrays length or missing data arrays");
      // Attempt partial transformation or return empty? Let's try partial.
      // You might want to return [] here for stricter validation.
    }

    return labels.flatMap((label, index) => {
      const rawValue1 = data1?.[index]; // Use optional chaining
      const rawValue2 = data2?.[index];

      // Convert to number, default to 0 if null, undefined, NaN, or invalid number
      let num1 = parseFloat(rawValue1);
      let num2 = parseFloat(rawValue2);

      // Ensure values are valid numbers, default to 0 otherwise
      const val1 = Number.isFinite(num1) ? num1 : 0;
      const val2 = Number.isFinite(num2) ? num2 : 0;

      // Attempt to parse date string into a JS Date object
      const dateObj = dayjs(label, "YYYY-MM-DD").toDate();

      return [
        { date: dateObj, value: val1, type: type1 },
        { date: dateObj, value: val2, type: type2 },
      ];
    });
  }, []);

  // --- NOUVELLE FONCTION: Pivoter les données pour Recharts LineChart ---
  const transformLineChartDataForRecharts = useCallback((data, type1Key, type2Key) => {
    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }
    const pivotedData = {};
    data.forEach(({ date, value, type }) => {
      const dateStr = dayjs(date).format("YYYY-MM-DD"); // Utiliser une clé string
      if (!pivotedData[dateStr]) {
        pivotedData[dateStr] = { date: date }; // Garder l'objet Date pour l'axe
      }
      // Assigner la valeur à la bonne clé (type1Key ou type2Key)
      if (type === type1Key) {
        pivotedData[dateStr][type1Key] = value;
      } else if (type === type2Key) {
        pivotedData[dateStr][type2Key] = value;
      }
    });
    // Convertir l'objet en tableau et trier par date
    return Object.values(pivotedData).sort((a, b) => a.date - b.date);
  }, []);

  const aggregatedSalesPurchasesChart = useMemo(
    () =>
      aggregateDashboardChart(
        dashboardData.salesPurchasesChart,
        ["salesData", "purchasesData"],
        chartGranularity,
      ),
    [dashboardData.salesPurchasesChart, chartGranularity],
  );
  const aggregatedPaymentsChart = useMemo(
    () =>
      aggregateDashboardChart(
        dashboardData.paymentsChart,
        ["receivedData", "sentData"],
        chartGranularity,
      ),
    [dashboardData.paymentsChart, chartGranularity],
  );

  // Transformer les données pour Recharts
  const salesPurchasesDataLong = transformChartData(
    aggregatedSalesPurchasesChart,
    "salesData",
    "purchasesData",
    "Ventes",
    "Achats"
  );
  const salesPurchasesDataRecharts = transformLineChartDataForRecharts(
    salesPurchasesDataLong,
    "Ventes",
    "Achats"
  );

  const paymentsDataLong = transformChartData(
    aggregatedPaymentsChart,
    "receivedData",
    "sentData",
    "Entrées",
    "Sorties"
  );
  const paymentsDataRecharts = transformLineChartDataForRecharts(
    paymentsDataLong,
    "Entrées",
    "Sorties"
  );

  // Process expense data safely
  const expenseData = (dashboardData.expenseBreakdown || [])
    .map((item) => {
      const amount = parseFloat(item.totalAmount);
      return {
        name: item.categoryName,
        value: Number.isFinite(amount) ? amount : 0,
        categoryId: item.categoryId,
      };
    })
    // Optional: Filter out items with zero amount to avoid tiny/invisible slices
    .filter((item) => item.value > 0);

  // Filtrer les données pour le graphique Pie en fonction des catégories inactives
  const filteredExpenseData = expenseData.filter(
    (entry) => !inactiveCategories.includes(entry.name)
  );

  // Calculer le total des dépenses filtrées pour l'affichage central
  const totalFilteredExpenses = filteredExpenseData.reduce(
    (sum, entry) => sum + entry.value,
    0
  );

  const businessCurrent = dashboardData.businessMetrics?.current;
  const businessComparisons = useMemo(
    () => dashboardData.businessMetrics?.comparisons || {},
    [dashboardData.businessMetrics?.comparisons],
  );
  const businessStatus = dashboardDataStatus.businessMetrics;
  const hasBusinessMetrics = isDashboardDataAvailable(businessStatus);

  const getBusinessMetricStatus = useCallback(
    (value, fallbackStatus) =>
      hasBusinessMetrics
        ? resolveDashboardMetricStatus(businessStatus, value)
        : fallbackStatus,
    [businessStatus, hasBusinessMetrics],
  );

  const primaryKpis = useMemo(() => {
    const totals = dashboardData.totals;
    const totalsStatus = dashboardDataStatus.totals;
    const fallbackTreasuryBalance = totals?.totalTreasuryBalance;
    const balanceStatus = resolveDashboardMetricStatus(
      totalsStatus,
      fallbackTreasuryBalance,
    );
    const resolvedBalance =
      businessCurrent?.treasury?.closingBalance ?? fallbackTreasuryBalance;
    const unavailableBusinessStatus = {
      status: DASHBOARD_DATA_STATUS.UNAVAILABLE,
      message: "L'indicateur fiabilisé n'est pas disponible.",
    };

    return [
      {
        key: "sales",
        label: "Ventes nettes",
        value: businessCurrent?.sales?.net ?? totals?.totalSales,
        dataStatus: getBusinessMetricStatus(
          businessCurrent?.sales?.net,
          resolveDashboardMetricStatus(totalsStatus, totals?.totalSales),
        ),
        comparison: hasBusinessMetrics ? businessComparisons.netSales : null,
        meta: hasBusinessMetrics
          ? `Brut ${formatCurrency(businessCurrent?.sales?.gross)} · retours ${formatCurrency(businessCurrent?.sales?.returns)}`
          : null,
        accent: "#1677ff",
        icon: <RiseOutlined />,
      },
      {
        key: "purchases",
        label: "Achats nets",
        value: businessCurrent?.purchases?.net ?? totals?.totalPurchases,
        dataStatus: getBusinessMetricStatus(
          businessCurrent?.purchases?.net,
          resolveDashboardMetricStatus(totalsStatus, totals?.totalPurchases),
        ),
        comparison: hasBusinessMetrics ? businessComparisons.netPurchases : null,
        meta: hasBusinessMetrics
          ? `Brut ${formatCurrency(businessCurrent?.purchases?.gross)} · retours ${formatCurrency(businessCurrent?.purchases?.returns)}`
          : null,
        accent: "#7c3aed",
        icon: <FallOutlined />,
      },
      {
        key: "expenses",
        label: "Dépenses engagées",
        value: businessCurrent?.expenses?.total ?? totals?.totalExpenses,
        dataStatus: getBusinessMetricStatus(
          businessCurrent?.expenses?.total,
          resolveDashboardMetricStatus(totalsStatus, totals?.totalExpenses),
        ),
        comparison: hasBusinessMetrics ? businessComparisons.totalExpenses : null,
        meta: hasBusinessMetrics
          ? `Payé ${formatCurrency(businessCurrent?.expenses?.paid)} · à payer ${formatCurrency(businessCurrent?.expenses?.due)}`
          : null,
        accent: "#b45309",
        icon: <WalletOutlined />,
      },
      {
        key: "treasuryBalance",
        label: "Solde de trésorerie",
        value: resolvedBalance,
        dataStatus: getBusinessMetricStatus(
          businessCurrent?.treasury?.closingBalance,
          balanceStatus,
        ),
        comparison: hasBusinessMetrics
          ? businessComparisons.treasuryBalance
          : null,
        comparisonContext: "vs clôture précédente",
        comparisonNewLabel: "Nouveau solde",
        meta: hasBusinessMetrics
          ? `Ouverture des comptes ${formatCurrency(businessCurrent?.treasury?.accountOpeningBalance)} · mouvements cumulés ${formatCurrency(businessCurrent?.treasury?.cumulativeMovementBalance)}`
          : null,
        accent: "#0f766e",
        icon: <DollarCircleOutlined />,
      },
      {
        key: "receivables",
        label: "Créances clients",
        value: businessCurrent?.sales?.receivables,
        dataStatus: hasBusinessMetrics
          ? resolveDashboardMetricStatus(
              businessStatus,
              businessCurrent?.sales?.receivables,
            )
          : unavailableBusinessStatus,
        comparison: hasBusinessMetrics ? businessComparisons.receivables : null,
        accent: "#0369a1",
        icon: <WalletOutlined />,
      },
      {
        key: "expensesDue",
        label: "Dépenses à payer",
        value: businessCurrent?.expenses?.due,
        dataStatus: hasBusinessMetrics
          ? resolveDashboardMetricStatus(
              businessStatus,
              businessCurrent?.expenses?.due,
            )
          : unavailableBusinessStatus,
        comparison: hasBusinessMetrics ? businessComparisons.expensesDue : null,
        accent: "#c2410c",
        icon: <AlertOutlined />,
      },
    ];
  }, [
    businessComparisons,
    businessCurrent,
    businessStatus,
    dashboardData.totals,
    dashboardDataStatus.totals,
    getBusinessMetricStatus,
    hasBusinessMetrics,
  ]);

  const periodPerformanceKpis = useMemo(
    () => [
      {
        key: "margin",
        definitionKey: "grossMargin",
        label: "Marge brute",
        value:
          businessCurrent?.margin?.grossMargin ??
          dashboardData.grossMargin?.grossMargin,
        dataStatus: getBusinessMetricStatus(
          businessCurrent?.margin?.grossMargin,
          resolveDashboardMetricStatus(
            dashboardDataStatus.grossMargin,
            dashboardData.grossMargin?.grossMargin,
          ),
        ),
        comparison: hasBusinessMetrics ? businessComparisons.grossMargin : null,
        coverage: hasBusinessMetrics
          ? businessCurrent?.margin?.costCoverage?.traceableCostLineRate ??
            businessCurrent?.margin?.costCoverage?.snapshotLineRate
          : null,
        meta: hasBusinessMetrics
          ? `${Number(businessCurrent?.margin?.grossMarginPercentage || 0).toFixed(1)} % de marge · coûts documentés ${Number(businessCurrent?.margin?.costCoverage?.traceableCostLineRate ?? businessCurrent?.margin?.costCoverage?.snapshotLineRate ?? 0).toFixed(1)} %`
          : dashboardData.grossMargin?.grossMarginPercentage !== undefined
            ? `${Number(dashboardData.grossMargin.grossMarginPercentage).toFixed(1)} %`
            : null,
        accent: "#2563eb",
        icon: <CalculatorOutlined />,
      },
      {
        key: "basket",
        definitionKey: "averageOrderValue",
        label: "Panier moyen net",
        value:
          businessCurrent?.averageOrderValue?.value ??
          dashboardData.averageOrderValue?.averageOrderValue,
        dataStatus: getBusinessMetricStatus(
          businessCurrent?.averageOrderValue?.value,
          resolveDashboardMetricStatus(
            dashboardDataStatus.averageOrderValue,
            dashboardData.averageOrderValue?.averageOrderValue,
          ),
        ),
        comparison: hasBusinessMetrics
          ? businessComparisons.averageOrderValue
          : null,
        meta: hasBusinessMetrics
          ? `${businessCurrent?.averageOrderValue?.orderCount || 0} commande(s)`
          : null,
        accent: "#7c3aed",
        icon: <ShoppingOutlined />,
      },
      {
        key: "turnover",
        definitionKey: "stockTurnover",
        label: "Rotation du stock",
        value:
          businessCurrent?.stockTurnover?.stockTurnoverRatio ??
          dashboardData.stockTurnover?.stockTurnoverRatio,
        dataStatus: getBusinessMetricStatus(
          businessCurrent?.stockTurnover?.stockTurnoverRatio,
          resolveDashboardMetricStatus(
            dashboardDataStatus.stockTurnover,
            dashboardData.stockTurnover?.stockTurnoverRatio,
          ),
        ),
        comparison: hasBusinessMetrics
          ? businessComparisons.stockTurnoverRatio
          : null,
        formatter: (value) => {
          const numericValue = Number(value);
          if (numericValue > 0 && numericValue < 0.01) return "< 0,01";
          return numericValue.toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 3,
          });
        },
        meta: hasBusinessMetrics
          ? `Stock moyen ${formatCurrency(
              businessCurrent?.stockTurnover?.averageInventoryValue,
            )} · couverture coûts historiques ${Math.min(
              Number(
                businessCurrent?.stockTurnover?.quality
                  ?.openingHistoricalCostCoverageRate ?? 100,
              ),
              Number(
                businessCurrent?.stockTurnover?.quality
                  ?.closingHistoricalCostCoverageRate ?? 100,
              ),
            ).toFixed(1)} %`
          : "ratio",
        accent: "#0f766e",
        icon: <SyncOutlined />,
      },
    ],
    [
      businessComparisons,
      businessCurrent,
      dashboardData.averageOrderValue,
      dashboardData.grossMargin,
      dashboardData.stockTurnover,
      dashboardDataStatus.averageOrderValue,
      dashboardDataStatus.grossMargin,
      dashboardDataStatus.stockTurnover,
      getBusinessMetricStatus,
      hasBusinessMetrics,
    ],
  );

  const currentStockKpis = useMemo(
    () => [
      {
        key: "costValue",
        definitionKey: "stockCost",
        label: "Stock au coût",
        value: dashboardData.stockValuation?.totalStockValueCost,
        dataStatus: resolveDashboardMetricStatus(
          dashboardDataStatus.stockValuation,
          dashboardData.stockValuation?.totalStockValueCost,
        ),
        accent: "#334155",
        icon: <DatabaseOutlined />,
      },
      {
        key: "saleValue",
        definitionKey: "salePotential",
        label: "Valeur de vente potentielle",
        value:
          dashboardData.stockValuation?.totalPotentialSaleValue ??
          dashboardData.stockValuation?.totalStockValueSale,
        dataStatus: resolveDashboardMetricStatus(
          dashboardDataStatus.stockValuation,
          dashboardData.stockValuation?.totalPotentialSaleValue ??
            dashboardData.stockValuation?.totalStockValueSale,
        ),
        accent: "#0369a1",
        icon: <TagsOutlined />,
      },
      {
        key: "thresholdCoverage",
        definitionKey: "stockThresholdCoverage",
        label: "Couverture des seuils",
        value: dashboardData.stockValuation?.quality?.thresholdCoverageRate,
        dataStatus: resolveDashboardMetricStatus(
          dashboardDataStatus.stockValuation,
          dashboardData.stockValuation?.quality?.thresholdCoverageRate,
        ),
        formatter: (value) => `${Number(value).toFixed(1)} %`,
        meta: `${Number(
          dashboardData.stockValuation?.quality?.thresholdConfiguredCount || 0,
        )}/${Number(
          dashboardData.stockValuation?.quality?.thresholdEligibleStockLineCount || 0,
        )} article(s) configuré(s)`,
        accent: "#b45309",
        icon: <AlertOutlined />,
      },
    ],
    [
      dashboardData.stockValuation,
      dashboardDataStatus.stockValuation,
    ],
  );

  const cachedDataTimestamp = useMemo(() => {
    const timestamps = Object.values(dashboardDataStatus)
      .filter((item) => item?.status === DASHBOARD_DATA_STATUS.CACHED)
      .map((item) => Number(item.updatedAt))
      .filter((value) => Number.isFinite(value) && value > 0);

    return timestamps.length > 0 ? Math.min(...timestamps) : null;
  }, [dashboardDataStatus]);

  const getEmptyStateDescription = useCallback(
    (key, verifiedEmptyMessage) =>
      dashboardDataStatus[key]?.status === DASHBOARD_DATA_STATUS.UNAVAILABLE
        ? "Données indisponibles"
        : verifiedEmptyMessage,
    [dashboardDataStatus],
  );

  const expenseLegendItems = useMemo(
    () =>
      expenseData.map((entry, index) => ({
        ...entry,
        color: COLORS[index % COLORS.length],
        inactive: inactiveCategories.includes(entry.name),
      })),
    [expenseData, inactiveCategories]
  );

  const desktopExpenseChartData = useMemo(() => {
    const sortedExpenses = [...filteredExpenseData].sort((a, b) => b.value - a.value);

    if (sortedExpenses.length <= MAX_DESKTOP_EXPENSE_BARS) {
      return sortedExpenses.map((entry, index) => ({
        ...entry,
        color: COLORS[index % COLORS.length],
      }));
    }

    const topExpenses = sortedExpenses.slice(0, MAX_DESKTOP_EXPENSE_BARS).map(
      (entry, index) => ({
        ...entry,
        color: COLORS[index % COLORS.length],
      })
    );
    const remainingExpenses = sortedExpenses.slice(MAX_DESKTOP_EXPENSE_BARS);
    const remainingTotal = remainingExpenses.reduce((sum, entry) => sum + entry.value, 0);

    return [
      ...topExpenses,
      {
        name: `Autres (${remainingExpenses.length})`,
        fullName: `Autres (${remainingExpenses.length} catégories)`,
        value: remainingTotal,
        color: "#98a2b3",
        isAggregated: true,
      },
    ];
  }, [filteredExpenseData]);

  const desktopExpenseChartHeight = useMemo(
    () => Math.max(300, desktopExpenseChartData.length * 42),
    [desktopExpenseChartData.length]
  );

  const desktopExpenseChartHint = useMemo(() => {
    if (filteredExpenseData.length <= MAX_DESKTOP_EXPENSE_BARS) {
      return `${filteredExpenseData.length} catégorie${
        filteredExpenseData.length > 1 ? "s" : ""
      } affichée${filteredExpenseData.length > 1 ? "s" : ""}`;
    }

    return `Top ${MAX_DESKTOP_EXPENSE_BARS} catégories sur ${filteredExpenseData.length}, le reste est regroupé dans "Autres"`;
  }, [filteredExpenseData.length]);

  // Formatter pour le Tooltip Recharts PieChart (nommé pour clarté)
  const pieTooltipFormatter = useCallback((value, name, props) => {
    return [formatCurrency(value), name];
  }, []);

  const barTooltipFormatter = useCallback((value) => {
    return [formatCurrency(value), "Montant"];
  }, []);

  const formatCompactAmount = useCallback((value) => {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return "0";
    }

    return new Intl.NumberFormat("fr-FR", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(numericValue);
  }, []);

  const formatExpenseCategoryTick = useCallback((value) => {
    if (typeof value !== "string") {
      return value;
    }

    return value.length > 18 ? `${value.slice(0, 18).trim()}…` : value;
  }, []);

  // Formatter pour le Tooltip Recharts LineChart (à rétablir)
  const lineTooltipFormatter = useCallback((value, name, props) => {
    // value: la valeur numérique de la ligne survolée
    // name: le nom de la ligne (ex: "Ventes", "Achats")
    // props: contient d'autres infos, notamment la couleur
    return [formatCurrency(value), name];
  }, []);

  // Gestionnaire pour le clic sur la légende
  const handleLegendClick = useCallback((e) => {
    const { value } = e; // Le nom de la catégorie cliquée
    setInactiveCategories(
      (prev) =>
        prev.includes(value)
          ? prev.filter((name) => name !== value) // Réactiver: le retirer des inactifs
          : [...prev, value] // Désactiver: l'ajouter aux inactifs
    );
  }, []);

  // Gestionnaires pour le survol du Pie
  const handlePieEnter = useCallback((_, index) => {
    setActivePieIndex(index);
  }, []);

  const handlePieLeave = useCallback(() => {
    setActivePieIndex(null);
  }, []);

  // --- Rendu ---
  if (isMobileView) {
    return (
      <div className="dashboard-mobile">
        <section className="dashboard-mobile__hero">
          <div className="dashboard-mobile__heroCopy">
            <span className="dashboard-mobile__eyebrow">Vue mobile</span>
            <Title level={2} className="dashboard-mobile__title">
              Tableau de bord
            </Title>
            <Text className="dashboard-mobile__subtitle">
              Suivez l&apos;activité commerciale sans repasser par une lecture desktop.
            </Text>
          </div>
          <Button
            type="default"
            onClick={fetchDashboardData}
            loading={loading}
            icon={<ReloadOutlined />}
            className="dashboard-mobile__refreshButton"
          >
            Actualiser
          </Button>

          <div className="dashboard-mobile__contextGrid">
            <div className="dashboard-mobile__contextCard">
              <span className="dashboard-mobile__contextLabel">Entreprise</span>
              <strong className="dashboard-mobile__contextValue">
                {selectedCompanyName}
              </strong>
            </div>
            <div className="dashboard-mobile__contextCard">
              <span className="dashboard-mobile__contextLabel">Établissement</span>
              <strong className="dashboard-mobile__contextValue">
                {selectedWarehouseName}
              </strong>
            </div>
            <div className="dashboard-mobile__contextCard">
              <span className="dashboard-mobile__contextLabel">Période</span>
              <strong className="dashboard-mobile__contextValue">
                {currentDateRangeLabel}
              </strong>
            </div>
          </div>
        </section>

        {error && !loading && (
          <Alert
            message="Erreur de chargement"
            description={error}
            type="error"
            showIcon
            closable
            className="dashboard-mobile__alert"
          />
        )}

        {cachedDataTimestamp && !loading && (
          <Alert
            message="Données en cache"
            description={`Dernière actualisation réussie : ${dayjs(
              cachedDataTimestamp,
            ).format("DD/MM/YYYY à HH:mm")}.`}
            type="warning"
            showIcon
            className="dashboard-mobile__alert"
          />
        )}

        <section className="dashboard-mobile__controls">
          <div className="dashboard-mobile__quickRanges">
            {QUICK_RANGE_OPTIONS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`dashboard-mobile__rangeChip${
                  activeQuickRangeKey === option.key
                    ? " dashboard-mobile__rangeChip--active"
                    : ""
                }`}
                onClick={() => applyQuickRange(option.key)}
              >
                {option.label}
              </button>
            ))}
          </div>

          <Button
            className="dashboard-mobile__filterToggle"
            onClick={() => setShowMobileFilters((current) => !current)}
          >
            {showMobileFilters ? "Masquer les filtres" : "Filtres avancés"}
          </Button>

          {showMobileFilters && (
            <Card bordered={false} className="dashboard-mobile__filtersCard">
              <div className="dashboard-mobile__filterField">
                <label className="dashboard-mobile__filterLabel">Établissement</label>
                <Select
                  placeholder="Tous les établissements assignés"
                  onChange={handleWarehouseChange}
                  value={selectedWarehouse ?? undefined}
                  style={{ width: "100%" }}
                  allowClear
                  disabled={
                    !selectedCompany || loading || warehousesForFilter.length === 0
                  }
                  loading={loading || !user}
                >
                  {warehousesForFilter.map((wh) => (
                    <Option key={wh.id} value={Number(wh.id)}>
                      {wh.name}
                    </Option>
                  ))}
                </Select>
              </div>

              <div className="dashboard-mobile__filterField">
                <label className="dashboard-mobile__filterLabel">Période</label>
                <RangePicker
                  style={{ width: "100%" }}
                  value={dateRange}
                  onChange={handleDateChange}
                  allowClear
                  disabled={loading}
                />
              </div>
            </Card>
          )}
        </section>

        <section className="dashboard-mobile__section">
          <div className="dashboard-mobile__sectionHead">
            <span className="dashboard-mobile__sectionEyebrow">Synthèse</span>
            <h3 className="dashboard-mobile__sectionTitle">Indicateurs clés</h3>
          </div>
          <div className="dashboard-mobile__kpiGrid">
            {primaryKpis.map((kpi) => (
              <DashboardMetricCard
                key={kpi.key}
                kpi={kpi}
                loading={loading}
                compact
                onDrilldown={handleMetricDrilldown}
              />
            ))}
          </div>
          {previousDateRangeLabel ? (
            <Text className="dashboard-period-comparison-note">
              Comparaison : {previousDateRangeLabel}
            </Text>
          ) : null}
        </section>

        <section className="dashboard-mobile__section">
          <div className="dashboard-mobile__sectionHead">
            <span className="dashboard-mobile__sectionEyebrow">Tendances</span>
            <h3 className="dashboard-mobile__sectionTitle">Vue d&apos;ensemble</h3>
          </div>

          <Card bordered={false} className="dashboard-mobile__panel">
            <div className="dashboard-mobile__panelHeader">
              <div>
                <span className="dashboard-mobile__panelEyebrow">Flux</span>
                <h4 className="dashboard-mobile__panelTitle">
                  Ventes nettes vs achats nets
                </h4>
              </div>
              <Tag>{formatDashboardChartGranularity(chartGranularity)}</Tag>
            </div>
            <Radio.Group
              value={salesPurchasesFilter}
              onChange={(e) => setSalesPurchasesFilter(e.target.value)}
              size="small"
              className="dashboard-mobile__radioGroup"
            >
              <Radio.Button value="Tous">Tous</Radio.Button>
              <Radio.Button value="Ventes">Ventes</Radio.Button>
              <Radio.Button value="Achats">Achats</Radio.Button>
            </Radio.Group>
            {loading ? (
              <Skeleton active paragraph={{ rows: 5 }} />
            ) : salesPurchasesDataRecharts.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart
                  data={salesPurchasesDataRecharts}
                  margin={{ top: 8, right: 8, bottom: 0, left: -18 }}
                >
                  <CartesianGrid stroke="var(--border-color)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={formatDateTick} />
                  <YAxis tickFormatter={formatYAxisTick} />
                  <Tooltip formatter={lineTooltipFormatter} />
                  {(salesPurchasesFilter === "Tous" ||
                    salesPurchasesFilter === "Ventes") && (
                    <RechartsLine
                      type="monotone"
                      dataKey="Ventes"
                      stroke="#2563eb"
                      strokeWidth={2.5}
                      dot={false}
                      name="Ventes"
                    />
                  )}
                  {(salesPurchasesFilter === "Tous" ||
                    salesPurchasesFilter === "Achats") && (
                    <RechartsLine
                      type="monotone"
                      dataKey="Achats"
                      stroke="#7c3aed"
                      strokeWidth={2.5}
                      strokeDasharray="7 5"
                      dot={false}
                      name="Achats"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "salesPurchasesChart",
                  "Aucune donnée de vente ou d'achat",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>

          <Card bordered={false} className="dashboard-mobile__panel">
            <div className="dashboard-mobile__panelHeader">
              <div>
                <span className="dashboard-mobile__panelEyebrow">Trésorerie</span>
                <h4 className="dashboard-mobile__panelTitle">
                  Entrées vs sorties de trésorerie
                </h4>
              </div>
              <Tag>{formatDashboardChartGranularity(chartGranularity)}</Tag>
            </div>
            <Radio.Group
              value={paymentsFilter}
              onChange={(e) => setPaymentsFilter(e.target.value)}
              size="small"
              className="dashboard-mobile__radioGroup"
            >
              <Radio.Button value="Tous">Tous</Radio.Button>
              <Radio.Button value="Entrées">Entrées</Radio.Button>
              <Radio.Button value="Sorties">Sorties</Radio.Button>
            </Radio.Group>
            {loading ? (
              <Skeleton active paragraph={{ rows: 5 }} />
            ) : paymentsDataRecharts.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <LineChart
                  data={paymentsDataRecharts}
                  margin={{ top: 8, right: 8, bottom: 0, left: -18 }}
                >
                  <CartesianGrid stroke="var(--border-color)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={formatDateTick} />
                  <YAxis tickFormatter={formatYAxisTick} />
                  <Tooltip formatter={lineTooltipFormatter} />
                  {(paymentsFilter === "Tous" ||
                    paymentsFilter === "Entrées") && (
                    <RechartsLine
                      type="monotone"
                      dataKey="Entrées"
                      stroke="#0f766e"
                      strokeWidth={2.5}
                      dot={false}
                      name="Entrées"
                    />
                  )}
                  {(paymentsFilter === "Tous" ||
                    paymentsFilter === "Sorties") && (
                    <RechartsLine
                      type="monotone"
                      dataKey="Sorties"
                      stroke="#c2410c"
                      strokeWidth={2.5}
                      strokeDasharray="7 5"
                      dot={false}
                      name="Sorties"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "paymentsChart",
                  "Aucun flux de trésorerie",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </section>

        <section className="dashboard-mobile__section">
          <div className="dashboard-mobile__sectionHead">
            <span className="dashboard-mobile__sectionEyebrow">Dépenses</span>
            <h3 className="dashboard-mobile__sectionTitle">Répartition active</h3>
          </div>

          <Card bordered={false} className="dashboard-mobile__panel">
            <div className="dashboard-mobile__panelHeader dashboard-mobile__panelHeader--spread">
              <div>
                <span className="dashboard-mobile__panelEyebrow">Analyse</span>
                <h4 className="dashboard-mobile__panelTitle">Répartition des dépenses</h4>
              </div>
              {inactiveCategories.length > 0 && (
                <Button
                  type="link"
                  size="small"
                  onClick={() => setInactiveCategories([])}
                >
                  Réinitialiser
                </Button>
              )}
            </div>

            {loading ? (
              <Skeleton.Node active className="dashboard-mobile__pieSkeleton">
                <div />
              </Skeleton.Node>
            ) : expenseData.length > 0 ? (
              <>
                <div className="dashboard-mobile__expenseTotal">
                  <span>Total visible</span>
                  <strong>{formatCurrency(totalFilteredExpenses)}</strong>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={filteredExpenseData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      innerRadius={54}
                      outerRadius={88}
                      fill="#8884d8"
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      onMouseEnter={handlePieEnter}
                      onMouseLeave={handlePieLeave}
                    >
                      {filteredExpenseData.map((entry, index) => (
                        <Cell
                          key={`mobile-cell-${entry.categoryId || index}`}
                          fill={COLORS[index % COLORS.length]}
                          fillOpacity={
                            activePieIndex === null || activePieIndex === index
                              ? 1
                              : 0.58
                          }
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={pieTooltipFormatter}
                      cursor={{ fill: "transparent" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="dashboard-mobile__legendGrid">
                  {expenseLegendItems.map((entry) => (
                    <button
                      key={entry.categoryId || entry.name}
                      type="button"
                      className={`dashboard-mobile__legendItem${
                        entry.inactive ? " dashboard-mobile__legendItem--inactive" : ""
                      }`}
                      onClick={() => handleLegendClick({ value: entry.name })}
                    >
                      <span
                        className="dashboard-mobile__legendDot"
                        style={{ backgroundColor: entry.color }}
                      />
                      <span className="dashboard-mobile__legendName">{entry.name}</span>
                      <strong className="dashboard-mobile__legendValue">
                        {formatCurrency(entry.value)}
                      </strong>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "expenseBreakdown",
                  "Aucune dépense enregistrée",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </section>

        <section className="dashboard-mobile__section">
          <div className="dashboard-mobile__sectionHead">
            <span className="dashboard-mobile__sectionEyebrow">Performance</span>
            <h3 className="dashboard-mobile__sectionTitle">Sur la période</h3>
          </div>
          <div className="dashboard-mobile__secondaryGrid">
            {periodPerformanceKpis.map((kpi) => (
              <DashboardMetricCard
                key={kpi.key}
                kpi={kpi}
                loading={loading}
                compact
                onDrilldown={handleMetricDrilldown}
              />
            ))}
          </div>
        </section>

        <section className="dashboard-mobile__section">
          <div className="dashboard-mobile__sectionHead">
            <span className="dashboard-mobile__sectionEyebrow">Stock actuel</span>
            <h3 className="dashboard-mobile__sectionTitle">Situation physique</h3>
          </div>
          <div className="dashboard-mobile__secondaryGrid">
            {currentStockKpis.map((kpi) => (
              <DashboardMetricCard
                key={kpi.key}
                kpi={kpi}
                loading={loading}
                compact
                onDrilldown={handleMetricDrilldown}
              />
            ))}
          </div>
          {!loading ? (
            <StockValuationDetails
              valuation={dashboardData.stockValuation}
              dataStatus={dashboardDataStatus.stockValuation}
            />
          ) : null}
        </section>

        <section className="dashboard-mobile__section">
          <div className="dashboard-mobile__sectionHead">
            <span className="dashboard-mobile__sectionEyebrow">Terrain</span>
            <h3 className="dashboard-mobile__sectionTitle">Points d'attention</h3>
          </div>

          <Card bordered={false} className="dashboard-mobile__panel">
            <div className="dashboard-mobile__panelHeader">
              <div>
                <span className="dashboard-mobile__panelEyebrow">Produits</span>
                <h4 className="dashboard-mobile__panelTitle">Top ventes</h4>
              </div>
            </div>
            {loading ? (
              <Skeleton active avatar paragraph={{ rows: 3 }} />
            ) : dashboardData.topSellingProducts?.length ? (
              <List
                itemLayout="horizontal"
                dataSource={dashboardData.topSellingProducts.slice(0, 4)}
                renderItem={(item, index) => (
                  <List.Item className="dashboard-mobile__listItem">
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          size="small"
                          className="dashboard-mobile__listAvatar dashboard-mobile__listAvatar--warning"
                        >
                          {index + 1}
                        </Avatar>
                      }
                      title={<span className="dashboard-mobile__listTitle">{item.name}</span>}
                      description={`Revenu: ${formatCurrency(
                        item.totalRevenue
                      )} • Qté: ${item.quantitySold}`}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "topSellingProducts",
                  "Aucun produit vendu",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>

          <Card bordered={false} className="dashboard-mobile__panel">
            <div className="dashboard-mobile__panelHeader">
              <div>
                <span className="dashboard-mobile__panelEyebrow">Clients</span>
                <h4 className="dashboard-mobile__panelTitle">Meilleurs clients</h4>
              </div>
            </div>
            {loading ? (
              <Skeleton active avatar paragraph={{ rows: 3 }} />
            ) : dashboardData.topCustomers?.length ? (
              <List
                itemLayout="horizontal"
                dataSource={dashboardData.topCustomers.slice(0, 4)}
                rowKey={(item) =>
                  item.customerKey || `${item.source || "classic"}:${item.customerId}`
                }
                renderItem={(item, index) => (
                  <List.Item className="dashboard-mobile__listItem">
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          size="small"
                          className="dashboard-mobile__listAvatar dashboard-mobile__listAvatar--info"
                        >
                          {index + 1}
                        </Avatar>
                      }
                      title={<span className="dashboard-mobile__listTitle">{item.name}</span>}
                      description={`Total: ${formatCurrency(
                        item.totalSpent
                      )} • ${item.salesCount} commande(s)`}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "topCustomers",
                  "Aucun client trouvé",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>

          <Card bordered={false} className="dashboard-mobile__panel">
            <div className="dashboard-mobile__panelHeader">
              <div>
                <span className="dashboard-mobile__panelEyebrow">Stock</span>
                <h4 className="dashboard-mobile__panelTitle">Alertes bas niveau</h4>
              </div>
            </div>
            {loading ? (
              <Skeleton active avatar paragraph={{ rows: 3 }} />
            ) : dashboardData.stockAlerts?.length ? (
              <List
                itemLayout="horizontal"
                dataSource={dashboardData.stockAlerts.slice(0, 4)}
                renderItem={(item) => (
                  <List.Item className="dashboard-mobile__listItem">
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          src={item.product_image || "/images/default_product.png"}
                          size="small"
                          shape="square"
                        />
                      }
                      title={<span className="dashboard-mobile__listTitle">{item.name}</span>}
                      description={
                        <span className="dashboard-mobile__stockMeta">
                          <Tag color={item.currentStock <= 0 ? "error" : "warning"}>
                            Stock: {item.currentStock}
                          </Tag>
                          <span>{item.warehouseName}</span>
                        </span>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "stockAlerts",
                  "Aucune alerte de stock",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </section>
      </div>
    );
  }

  return (
    <div className="dashboard-desktop">
      <header className="dashboard-desktop__header">
        <div>
          <Title level={2} className="dashboard-desktop__title">
            Tableau de bord
          </Title>
        </div>
        <Button
          onClick={fetchDashboardData}
          loading={loading}
          icon={<ReloadOutlined />}
        >
          Actualiser
        </Button>
      </header>

      {/* Filter Row - MODIFIED */}
      <Card className="dashboard-desktop__filters" bordered={false}>
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={12} md={8} lg={6}>
            <label style={{ display: "block", marginBottom: "4px" }}>
              Établissement
            </label>
            <Select
              placeholder="Tous les établissements assignés"
              onChange={handleWarehouseChange}
              value={selectedWarehouse ?? undefined} // Use undefined for placeholder
              style={{ width: "100%" }}
              allowClear
              // Disable if no company selected, general loading, or no warehouses available for this user/company combo
              disabled={
                !selectedCompany || loading || warehousesForFilter.length === 0
              }
              // Show loading indicator specifically if general loading OR user data hasn't loaded yet
              loading={loading || !user}
            >
              {/* Map over the derived warehousesForFilter state */}
              {warehousesForFilter.map((wh) => (
                <Option key={wh.id} value={Number(wh.id)}>
                  {wh.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col flex="auto">
            <div className="dashboard-desktop__quickRanges">
              {QUICK_RANGE_OPTIONS.map((option) => (
                <Button
                  key={option.key}
                  type={activeQuickRangeKey === option.key ? "primary" : "default"}
                  size="small"
                  onClick={() => applyQuickRange(option.key)}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <label style={{ display: "block", marginBottom: "4px" }}>
              Période
            </label>
            <RangePicker
              style={{ width: "100%" }}
              value={dateRange}
              onChange={handleDateChange}
              allowClear={true} // Allow clearing the date range
              disabled={loading}
              // Default date format can be customized if needed: format="DD/MM/YYYY"
              ranges={{
                "Aujourd'hui": [dayjs(), dayjs()],
                "7 derniers jours": [dayjs().subtract(6, "days"), dayjs()],
                "30 derniers jours": [dayjs().subtract(29, "days"), dayjs()],
                "Ce mois": [dayjs().startOf("month"), dayjs().endOf("month")],
                "Mois dernier": [
                  dayjs().subtract(1, "month").startOf("month"),
                  dayjs().subtract(1, "month").endOf("month"),
                ],
                "Cette année": [dayjs().startOf("year"), dayjs().endOf("year")],
              }}
            />
          </Col>
          {/* Optional: Add a refresh button */}
          {/* <Col xs={24} sm={4} md={4} lg={3}>
              <Button type="primary" onClick={fetchDashboardData} loading={loading} icon={<ReloadOutlined />}>
                Actualiser
              </Button>
            </Col> */}
        </Row>
      </Card>

      {/* Loading and Error States */}
      {error && !loading && (
        <Alert
          message="Erreur de chargement"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginBottom: "20px" }}
        />
      )}

      {cachedDataTimestamp && !loading && (
        <Alert
          message="Données en cache"
          description={`Dernière actualisation réussie : ${dayjs(
            cachedDataTimestamp,
          ).format("DD/MM/YYYY à HH:mm")}.`}
          type="warning"
          showIcon
          style={{ marginBottom: "20px" }}
        />
      )}

      <section className="dashboard-desktop__section">
        <div className="dashboard-desktop__sectionHead">
          <div>
            <span>Activité sur la période</span>
            <h3>Indicateurs clés</h3>
          </div>
          <Text type="secondary">
            {previousDateRangeLabel
              ? `Comparée à la période du ${previousDateRangeLabel}`
              : "Montants nets et règlements réellement enregistrés"}
          </Text>
        </div>
      <div className="dashboard-desktop__metricGrid">
        {primaryKpis.map((kpi) => (
          <DashboardMetricCard
            key={kpi.key}
            kpi={kpi}
            loading={loading}
            onDrilldown={handleMetricDrilldown}
          />
        ))}
      </div>
      </section>

      {/* Ligne 2: Graphiques Lignes */}
      <section className="dashboard-desktop__section">
        <div className="dashboard-desktop__sectionHead">
          <div>
            <span>Tendances</span>
            <h3>Évolution des flux</h3>
          </div>
          <Tag>{formatDashboardChartGranularity(chartGranularity)}</Tag>
        </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <MetricDefinitionTitle
                metricKey="sales"
                label="Ventes nettes vs achats nets"
              />
            }
            bordered={false}
            hoverable
            extra={
              <Radio.Group
                value={salesPurchasesFilter}
                onChange={(e) => setSalesPurchasesFilter(e.target.value)}
                size="small"
              >
                <Radio.Button value="Tous">Tous</Radio.Button>
                <Radio.Button value="Ventes">Ventes</Radio.Button>
                <Radio.Button value="Achats">Achats</Radio.Button>
              </Radio.Group>
            }
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : salesPurchasesDataRecharts.length > 0 ? (
              // Graphique Recharts
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={salesPurchasesDataRecharts}
                  margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                >
                  <CartesianGrid stroke="var(--border-color)" />
                  <XAxis dataKey="date" tickFormatter={formatDateTick} />
                  <YAxis tickFormatter={formatYAxisTick} />
                  <Tooltip formatter={lineTooltipFormatter} />
                  <Legend />
                  {(salesPurchasesFilter === "Tous" ||
                    salesPurchasesFilter === "Ventes") && (
                    <RechartsLine
                      type="monotone"
                      dataKey="Ventes"
                      stroke="#2563eb"
                      strokeWidth={2.5}
                      yAxisId={0}
                      name="Ventes"
                    />
                  )}
                  {(salesPurchasesFilter === "Tous" ||
                    salesPurchasesFilter === "Achats") && (
                    <RechartsLine
                      type="monotone"
                      dataKey="Achats"
                      stroke="#7c3aed"
                      strokeWidth={2.5}
                      strokeDasharray="7 5"
                      yAxisId={0}
                      name="Achats"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "salesPurchasesChart",
                  "Aucune donnée de vente ou d'achat",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card
            title={
              <MetricDefinitionTitle
                metricKey="cashFlow"
                label="Entrées vs sorties de trésorerie"
              />
            }
            bordered={false}
            hoverable
            extra={
              <Radio.Group
                value={paymentsFilter}
                onChange={(e) => setPaymentsFilter(e.target.value)}
                size="small"
              >
                <Radio.Button value="Tous">Tous</Radio.Button>
                <Radio.Button value="Entrées">Entrées</Radio.Button>
                <Radio.Button value="Sorties">Sorties</Radio.Button>
              </Radio.Group>
            }
          >
            {loading ? (
              <Skeleton active paragraph={{ rows: 6 }} />
            ) : paymentsDataRecharts.length > 0 ? (
              // Graphique Recharts
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={paymentsDataRecharts}
                  margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
                >
                  <CartesianGrid stroke="var(--border-color)" />
                  <XAxis dataKey="date" tickFormatter={formatDateTick} />
                  <YAxis tickFormatter={formatYAxisTick} />
                  <Tooltip formatter={lineTooltipFormatter} />
                  <Legend />
                  {(paymentsFilter === "Tous" ||
                    paymentsFilter === "Entrées") && (
                    <RechartsLine
                      type="monotone"
                      dataKey="Entrées"
                      stroke="#0f766e"
                      strokeWidth={2.5}
                      yAxisId={0}
                      name="Entrées"
                    />
                  )}
                  {(paymentsFilter === "Tous" ||
                    paymentsFilter === "Sorties") && (
                    <RechartsLine
                      type="monotone"
                      dataKey="Sorties"
                      stroke="#c2410c"
                      strokeWidth={2.5}
                      strokeDasharray="7 5"
                      yAxisId={0}
                      name="Sorties"
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "paymentsChart",
                  "Aucun flux de trésorerie",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </Col>
      </Row>
      </section>

      <section className="dashboard-desktop__section">
        <div className="dashboard-desktop__sectionHead">
          <div>
            <span>Performance sur la période</span>
            <h3>Rentabilité et efficacité</h3>
          </div>
          <Text type="secondary">
            Marge canonique et rotation sur stock moyen
          </Text>
        </div>
        <div className="dashboard-desktop__metricGrid dashboard-desktop__metricGrid--three">
          {periodPerformanceKpis.map((kpi) => (
            <DashboardMetricCard
              key={kpi.key}
              kpi={kpi}
              loading={loading}
              onDrilldown={handleMetricDrilldown}
            />
          ))}
        </div>
      </section>

      <section className="dashboard-desktop__section">
        <div className="dashboard-desktop__sectionHead">
          <div>
            <span>Analyse et attention</span>
            <h3>Dépenses, classements et alertes</h3>
          </div>
        </div>
        <Row gutter={[16, 16]}>
        {/* Ligne 3: Listes & Graphique Dépenses */}
        <Col xs={24} md={12} lg={8}>
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                Répartition des Dépenses
                {/* Afficher le bouton Reset seulement si un filtre est actif */}
                {inactiveCategories.length > 0 && (
                  <Button
                    type="link"
                    icon={<ReloadOutlined />}
                    size="small"
                    onClick={() => setInactiveCategories([])} // Action Reset
                    style={{ marginLeft: "8px" }}
                  >
                    Réinitialiser
                  </Button>
                )}
              </div>
            }
            bordered={false}
            hoverable
            bodyStyle={{ position: "relative", paddingBottom: "10px" }}
          >
            {loading ? (
              <Skeleton.Node active style={{ width: 200, height: 200 }}>
                <div
                  style={{ width: "100%", height: "100%", borderRadius: "50%" }}
                />
              </Skeleton.Node>
            ) : expenseData.length > 0 ? (
              <div className="dashboard-expense-panel">
                <div className="dashboard-expense-panel__total">
                  <span>Total visible</span>
                  <strong>{formatCurrency(totalFilteredExpenses)}</strong>
                </div>
                <div className="dashboard-expense-panel__hint">{desktopExpenseChartHint}</div>
                {filteredExpenseData.length > 0 ? (
                  <ResponsiveContainer
                    width="100%"
                    height={desktopExpenseChartHeight}
                    key={`expense-bar-${selectedWarehouse || "all"}-${
                      dateRange ? dateRange[0].format() : "nodate"
                    }-${dateRange ? dateRange[1].format() : "nodate"}`}
                  >
                    <BarChart
                      data={desktopExpenseChartData}
                      layout="vertical"
                      margin={{ top: 12, right: 18, bottom: 4, left: 12 }}
                    >
                      <CartesianGrid
                        stroke="var(--border-color)"
                        strokeDasharray="3 3"
                        horizontal={false}
                      />
                      <XAxis
                        type="number"
                        tickFormatter={formatCompactAmount}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={130}
                        tickFormatter={formatExpenseCategoryTick}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        formatter={barTooltipFormatter}
                        cursor={{ fill: "rgba(15, 23, 42, 0.04)" }}
                        labelFormatter={(label) => label}
                        wrapperStyle={{ outline: "none", zIndex: 1000 }}
                        contentStyle={{
                          backgroundColor: "var(--bg-primary)",
                          border: "1px solid var(--border-color)",
                          borderRadius: "4px",
                          padding: "8px 12px",
                          boxShadow: "var(--shadow-md)",
                          fontSize: "12px",
                        }}
                      />
                      <Bar dataKey="value" radius={[0, 8, 8, 0]} barSize={18}>
                        {desktopExpenseChartData.map((entry, index) => (
                          <Cell
                            key={`expense-bar-cell-${entry.categoryId || entry.name || index}`}
                            fill={entry.color}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Empty
                    description="Toutes les catégories sont actuellement masquées"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </div>
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "expenseBreakdown",
                  "Aucune dépense enregistrée",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card
            title="Produits les plus vendus"
            bordered={false}
            hoverable
            bodyStyle={{
              padding: "0 20px 10px 20px", // Consistent padding
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {loading ? (
              <Skeleton active avatar paragraph={{ rows: 3 }} />
            ) : dashboardData.topSellingProducts &&
              dashboardData.topSellingProducts.length > 0 ? (
              <List
                itemLayout="horizontal"
                dataSource={dashboardData.topSellingProducts}
                renderItem={(item, index) => (
                  <List.Item style={{ padding: "8px 20px" }}>
                    {" "}
                    {/* Reduced padding */}
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          size="small"
                          style={{
                            backgroundColor: "var(--bg-tertiary)",
                            color: "var(--color-warning)",
                          }}
                        >
                          {index + 1}
                        </Avatar>
                      }
                      title={
                        <AntTooltip title={item.name}>{item.name}</AntTooltip>
                      }
                      description={`Revenu: ${formatCurrency(
                        item.totalRevenue
                      )} | Qté: ${item.quantitySold}`}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "topSellingProducts",
                  "Aucun produit vendu",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: "20px" }}
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={12} lg={8}>
          <Card
            title="Meilleurs Clients"
            bordered={false}
            hoverable
            bodyStyle={{
              padding: "0 20px 10px 20px", // Consistent padding
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {loading ? (
              <Skeleton active avatar paragraph={{ rows: 3 }} />
            ) : dashboardData.topCustomers &&
              dashboardData.topCustomers.length > 0 ? (
              <List
                itemLayout="horizontal"
                dataSource={dashboardData.topCustomers}
                rowKey={(item) =>
                  item.customerKey || `${item.source || "classic"}:${item.customerId}`
                }
                renderItem={(item, index) => (
                  <List.Item style={{ padding: "8px 20px" }}>
                    <List.Item.Meta
                      avatar={
                        <Avatar
                          size="small"
                          style={{
                            backgroundColor: "var(--bg-tertiary)",
                            color: "var(--color-info)",
                          }}
                        >
                          {index + 1}
                        </Avatar>
                      }
                      title={
                        <AntTooltip title={item.name}>{item.name}</AntTooltip>
                      }
                      description={`Total: ${formatCurrency(
                        item.totalSpent
                      )} | ${item.salesCount} commande(s)`}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "topCustomers",
                  "Aucun client trouvé",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: "20px" }}
              />
            )}
          </Card>
        </Col>

        {/* Ligne 4: KPIs Sup & Alertes Stock */}
        <Col xs={24} md={12} lg={8}>
          <Card
            title="Alertes de Stock Bas"
            bordered={false}
            hoverable
            bodyStyle={{
              padding: "0 20px 10px 20px", // Consistent padding
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {loading ? (
              <Skeleton active avatar paragraph={{ rows: 3 }} />
            ) : dashboardData.stockAlerts &&
              dashboardData.stockAlerts.length > 0 ? (
              <List
                itemLayout="horizontal"
                dataSource={dashboardData.stockAlerts}
                renderItem={(item) => (
                  <List.Item style={{ padding: "8px 20px" }}>
                    <List.Item.Meta
                      avatar={
                        // Optional: Show product image
                        <Avatar
                          src={
                            item.product_image || "/images/default_product.png"
                          }
                          size="small"
                          shape="square"
                        />
                      }
                      title={
                        <AntTooltip
                          title={`${item.name} (${item.warehouseName})`}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              maxWidth: "180px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              verticalAlign: "bottom",
                            }}
                          >
                            {item.name}
                          </span>
                        </AntTooltip>
                      }
                      description={
                        <Tag
                          color={item.currentStock <= 0 ? "error" : "warning"}
                          style={{ marginRight: 0 }}
                        >
                          Stock: {item.currentStock} (Alerte:{" "}
                          {item.alertQuantity})
                        </Tag>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description={getEmptyStateDescription(
                  "stockAlerts",
                  "Aucune alerte de stock",
                )}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: "20px" }}
              />
            )}
          </Card>
        </Col>
      </Row>
      </section>

      <section className="dashboard-desktop__section">
        <div className="dashboard-desktop__sectionHead">
          <div>
            <span>Situation actuelle</span>
            <h3>Stock physique et qualité de configuration</h3>
          </div>
          <Text type="secondary">
            Photographie indépendante de la période sélectionnée
          </Text>
        </div>
        <div className="dashboard-desktop__metricGrid dashboard-desktop__metricGrid--three">
          {currentStockKpis.map((kpi) => (
            <DashboardMetricCard
              key={kpi.key}
              kpi={kpi}
              loading={loading}
              onDrilldown={handleMetricDrilldown}
            />
          ))}
        </div>
      {!loading ? (
        <StockValuationDetails
          valuation={dashboardData.stockValuation}
          dataStatus={dashboardDataStatus.stockValuation}
        />
      ) : null}
      </section>
    </div>
  );
};

export default Dashboard;
